import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  MenuItem,
  Select,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import { TimesheetItem } from '../../TimesheetList';
import EnterHoursModal from './EnterHoursModal';
import MarkNoShowModal from './MarkNoShowModal';
import DismissWorkerModal from './DismissWorkerModal';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { JobStatus } from '../../util';

// The AUTHORITATIVE Resolve Timesheet workflow (GRAVY-10330).
//
// Opened from Job Detail's worker table and, since the QA retest, from the
// Dashboard V2 Missing Timesheet pencil. Dashboard V2 previously ran a
// simplified parallel flow of its own; QA failed that twice, and rightly — the
// copy, the staged Dismiss steps, the required details, the 50% credit notice,
// the worker card and the dependability warning had all drifted. There is one
// implementation now, and the Dashboard passes two extra props rather than
// forking it.
//
// Those two props are the ONLY Dashboard-specific adaptation, which is exactly
// the scope QA asked for:
//
//   * `shiftId` — the exact occurrence. A queue row is a (worker, occurrence)
//     pair; Job Detail is single-shift and omits it.
//   * `onCompleted` — refresh instead of `window.location.reload()`, so the
//     queue keeps its tab, filters, page and scroll position.
//
// Omit both and every request and side effect is byte-identical to what Job
// Detail sent before.
type ResolveTimesheetModalProps = {
  open: boolean;
  onClose: () => void;
  workerFullName: string;
  hasMissingCheckout: boolean;
  hasMissingTimesheet: boolean;
  isClientAdmin: boolean;
  timesheet?: TimesheetItem;
  job: any;
  workerInfo?: any;
  /** The exact occurrence to resolve. See the note above. */
  shiftId?: number;
  /** Replaces the full page reload on success. See the note above. */
  onCompleted?: () => void;
};

const ResolveTimesheetModal: React.FC<ResolveTimesheetModalProps> = ({
  open,
  onClose,
  workerFullName,
  hasMissingCheckout,
  hasMissingTimesheet,
  isClientAdmin,
  timesheet,
  job,
  workerInfo,
  shiftId,
  onCompleted,
}) => {
  const [action, setAction] = useState<string>('');
  const [selectContainer, setSelectContainer] = useState<HTMLElement | null>(
    null,
  );
  const [showError, setShowError] = useState(false);
  const [showEnterHoursModal, setShowEnterHoursModal] = useState(false);
  const [showMarkNoShowModal, setShowMarkNoShowModal] = useState(false);
  const [showDismissWorkerModal, setShowDismissWorkerModal] = useState(false);

  useEffect(() => {
    // Set container to document.body to render outside modal
    setSelectContainer(document.body);
  }, []);

  useEffect(() => {
    // Reset error when action is selected
    if (action) {
      setShowError(false);
    }
  }, [action]);

  const handleNext = () => {
    if (!action) {
      setShowError(true);
      return;
    }

    if (action === 'Enter Hours') {
      setShowEnterHoursModal(true);
    } else if (action === 'Mark as No-Show') {
      setShowMarkNoShowModal(true);
    } else if (action === 'Dismiss Worker') {
      // Only show dismiss worker modal if job is completed
      if (job?.status === JobStatus.COMPLETED || job?.status === 'COMPLETED') {
        setShowDismissWorkerModal(true);
      } else {
        // Handle dismiss worker for non-completed jobs if needed
        onClose();
      }
    } else {
      // Handle other actions here
      onClose();
    }
  };

  const handleCloseEnterHours = () => {
    setShowEnterHoursModal(false);
    setAction('');
    onClose(); // Close the parent modal when EnterHoursModal closes
  };

  const handleCloseMarkNoShow = () => {
    setShowMarkNoShowModal(false);
    setAction('');
    onClose(); // Close the parent modal when MarkNoShowModal closes
  };

  const handleCloseDismissWorker = () => {
    setShowDismissWorkerModal(false);
    setAction('');
    onClose(); // Close the parent modal when DismissWorkerModal closes
  };

  const handleConfirmNoShow = async () => {
    try {
      const workerId = workerInfo?.id;

      if (!workerId) {
        console.error('Shift Worker ID or Worker ID not found');
        return;
      }

      if (!job.id) {
        console.error('Shift ID not found');
        return;
      }

      await axiosRubyClient.post('/api/admin/job_workers/mark_as_no_show', {
        jobId: job.id,
        workerId: workerId,
      });

      handleCloseMarkNoShow();
      // Job Detail reloads because it IS the page; the Dashboard queue supplies
      // `onCompleted` so it can refresh without losing tab/filters/page/scroll.
      //
      // No `shiftId` on this call by design: `JobWorkers::MarkAsNoShowService`
      // marks the `job_workers` row, which has no shift dimension, so sending
      // one would imply per-occurrence behaviour the endpoint does not have.
      if (onCompleted) {
        onCompleted();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error marking worker as no-show:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEnterHoursResolve = () => {
    // Handle resolve action
    handleCloseEnterHours();
  };

  const handleEnterHoursSave = () => {
    // Handle save changes action
    handleCloseEnterHours();
  };

  const handleEnterHoursReject = () => {
    // Handle reject action
    handleCloseEnterHours();
  };

  const handleEnterHoursApprove = () => {
    // Handle approve action
    handleCloseEnterHours();
  };

  const description = useMemo(() => {
    if (hasMissingCheckout) {
      return `A missing check-out was detected for ${workerFullName}. Add the missing time and review the entry to resolve the issue.`;
    }
    if (hasMissingTimesheet) {
      return `A missing timesheet was detected for ${workerFullName}. Add the required information to resolve the issue.`;
    }
    return `Resolve ${workerFullName}'s timesheet.`;
  }, [workerFullName, hasMissingCheckout, hasMissingTimesheet]);

  return (
    <>
      <Dialog
        open={
          open &&
          !showEnterHoursModal &&
          !showMarkNoShowModal &&
          !showDismissWorkerModal
        }
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        // Stable hooks for the end-to-end suite. Both entry points (Job Detail
        // and the Dashboard V2 queue) render the SAME element, so one selector
        // covers both — which is the point of there being one modal.
        PaperProps={{ 'data-testid': 'resolveTimesheetModal' } as never}
      >
        <DialogContent sx={{ padding: '20px 20px 16px 20px' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Resolve Timesheet
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: '16px', mb: 0.5 }}
          >
            How do you want to resolve {workerFullName}'s timesheet?
          </Typography>
          <Typography variant="body2" sx={{ color: '#4B4B4B', mb: 2.5 }}>
            {description}
          </Typography>

          <Select
            fullWidth
            displayEmpty
            value={action}
            onChange={(e) => setAction(String(e.target.value))}
            renderValue={(selected) =>
              selected ? selected : 'Select action to resolve'
            }
            error={showError}
            MenuProps={{
              container: selectContainer,
              style: {
                zIndex: 99999,
              },
              PaperProps: {
                style: {
                  maxHeight: 200,
                  zIndex: 99999,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            sx={{
              height: 44,
              borderRadius: '8px',
              bgcolor: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: showError ? '#DC1515' : '#e0e0e0',
                borderWidth: showError ? '2px' : '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: showError ? '#DC1515' : '#45A735',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: showError ? '#DC1515' : '#45A735',
              },
            }}
          >
            <MenuItem value={''} disabled>
              Select action to resolve
            </MenuItem>
            <MenuItem value={'Enter Hours'}>Enter Hours</MenuItem>
            <MenuItem value={'Dismiss Worker'}>Dismiss Worker</MenuItem>
            <MenuItem value={'Mark as No-Show'}>Mark as No-Show</MenuItem>
          </Select>
          {showError && (
            <Typography
              sx={{
                color: '#DC1515',
                fontSize: '12px',
                mt: 0.5,
                mb: 0,
              }}
            >
              Please make a selection to continue.
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <MuiButton
                fullWidth
                onClick={onClose}
                variant="outlined"
                sx={{
                  borderColor: '#45A735',
                  color: '#45A735',
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 40,
                }}
              >
                Cancel
              </MuiButton>
            </Grid>
            <Grid item xs={6}>
              <MuiButton
                fullWidth
                variant="contained"
                onClick={handleNext}
                sx={{
                  bgcolor: '#45A735',
                  '&:hover': { bgcolor: '#45A735' },
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 40,
                }}
              >
                Next
              </MuiButton>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <EnterHoursModal
        open={showEnterHoursModal}
        onClose={handleCloseEnterHours}
        onResolve={handleEnterHoursResolve}
        onSaveChanges={handleEnterHoursSave}
        onReject={handleEnterHoursReject}
        onApprove={handleEnterHoursApprove}
        workerFullName={workerFullName}
        timesheet={timesheet}
        job={job}
        isClientAdmin={isClientAdmin}
        hasMissingCheckout={hasMissingCheckout}
        hasMissingTimesheet={hasMissingTimesheet}
        workerInfo={workerInfo}
        shiftId={shiftId}
        onCompleted={onCompleted}
      />

      <MarkNoShowModal
        open={showMarkNoShowModal}
        onClose={handleCloseMarkNoShow}
        onConfirm={handleConfirmNoShow}
        workerFullName={workerFullName}
      />

      <DismissWorkerModal
        open={showDismissWorkerModal}
        onClose={handleCloseDismissWorker}
        onConfirm={(reason, details, blockWorker) => {
          // API call is handled inside DismissWorkerModal
          handleCloseDismissWorker();
        }}
        workerFullName={workerFullName}
        workerInfo={workerInfo}
        job={job}
        onCompleted={onCompleted}
      />
    </>
  );
};

export default ResolveTimesheetModal;
